import {
  analyticsApi,
  projectApi,
  type AnalyticsRangeSelection,
} from '@shared/api-client';
import { analyticsRangeLabel } from '@shared/api-client/analytics-range';
import { useQuery } from '@tanstack/react-query';
import { Activity, Code2 } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { BreakdownPanel } from '@/components/analytics/breakdown-panel';
import { useAssistantPageContext } from '@/components/assistant/assistant-context';
import { LiveActivity } from '@/components/analytics/live-activity';
import { AnalyticsRangeSelect } from '@/components/analytics/range-select';
import { DashboardHeaderActions } from '@/components/dashboard-header-actions';
import { SetupSignalStatus } from '@/components/setup-signal-status';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { InstallSnippet } from './components/install-snippet';
import { GrowthOverview } from './components/growth-overview';
import { FindingsFeed } from './components/findings-feed';
import { SearchPerformance } from './components/search-performance';
import { ExternalSignals } from './components/external-signals';
import { RevenueOverview } from './components/revenue-overview';
import { useSession } from '@/hooks/use-session';

const ProjectSetup = ({
  projectId,
  publicKey,
}: {
  projectId: string;
  publicKey: string | undefined;
}) => (
  <Card className="overflow-hidden shadow-none">
    <EmptyState
      icon={Code2}
      title="Waiting for the first production events"
      description="Install the browser SDK on this product. This page will populate automatically after its first acknowledged batch reaches analytics."
      action={
        publicKey ? (
          <div className="w-full max-w-xl text-left">
            <InstallSnippet publicKey={publicKey} />
          </div>
        ) : (
          <Button asChild variant="secondary">
            <Link
              to={`/projects/${encodeURIComponent(projectId)}/data-sources`}
            >
              Create a project key
            </Link>
          </Button>
        )
      }
      className="py-20"
    />
  </Card>
);

export const OverviewPage = ({ detailed = false }: { detailed?: boolean }) => {
  const { user } = useSession();
  const { projectId } = useParams<{ projectId: string }>();
  const [range, setRange] = React.useState<AnalyticsRangeSelection>({
    key: '7d',
  });

  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });
  const analyticsQuery = useQuery({
    queryKey: ['analytics', 'project', projectId, range],
    queryFn: () => analyticsApi.getProject(projectId!, range),
    enabled: Boolean(projectId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const realtimeQuery = useQuery({
    queryKey: ['analytics', 'project', projectId, 'realtime'],
    queryFn: () => analyticsApi.getRealtime(projectId!),
    enabled: Boolean(projectId),
    staleTime: 0,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const apiKeysQuery = useQuery({
    queryKey: ['api-keys', projectId],
    queryFn: () => projectApi.listApiKeys(projectId!),
    enabled: Boolean(projectId),
  });

  const project = projectsQuery.data?.projects.find(
    (candidate) => candidate.id === projectId,
  );
  const activeKey = apiKeysQuery.data?.apiKeys.find((key) => !key.revokedAt);
  const analytics = analyticsQuery.data;
  const summary = analytics?.project.summary;

  useAssistantPageContext({
    page: detailed ? 'Reports' : 'Overview',
    projectId: projectId ?? '',
    projectName: project?.name ?? 'Project',
    range: range.key,
    rangeLabel: analyticsRangeLabel(range),
  });

  if (projectsQuery.isLoading) {
    return <Skeleton className="h-[560px] rounded-card" />;
  }

  if (!project) {
    return (
      <Card className="shadow-none">
        <EmptyState
          icon={Activity}
          title="Project not found"
          description="It may have been deleted or belong to another organization."
          action={
            <Button asChild variant="secondary">
              <Link to="/">Back to projects</Link>
            </Button>
          }
        />
      </Card>
    );
  }

  const mobile = project.profile?.platform === 'mobile';
  const canManage = ['owner', 'admin'].includes(
    user?.currentOrganization.role ?? '',
  );
  if (!detailed && canManage && project.profile?.setupStep === 'identity') {
    return <Navigate to={`/projects/${project.id}/setup`} replace />;
  }

  return (
    <>
      <h1
        className={detailed ? 'mb-6 text-xl font-semibold text-ink' : 'sr-only'}
      >
        {detailed ? 'Reports' : 'Overview'}
      </h1>
      <DashboardHeaderActions>
        {!mobile && (
          <LiveActivity
            projectId={project.id}
            realtime={realtimeQuery.data}
            isError={realtimeQuery.isError}
          />
        )}
        <div className="flex min-w-0 justify-end">
          <AnalyticsRangeSelect value={range} onValueChange={setRange} />
        </div>
      </DashboardHeaderActions>
      <div className="space-y-6">
        {!detailed && project.profile?.setupStep === 'install' && canManage && (
          <Alert>
            Your project is ready to connect.{' '}
            <Link
              className="font-medium text-accent underline"
              to={`/projects/${project.id}/setup`}
            >
              Continue setup
            </Link>
          </Alert>
        )}
        {!detailed && canManage && project.profile?.trackingPlan && (
          <SetupSignalStatus project={project} compact />
        )}
        {!detailed && mobile && analytics && (
          <RevenueOverview projectId={project.id} range={analytics.range} />
        )}
        {analyticsQuery.isError ? (
          <Alert tone="error">
            {analyticsQuery.error instanceof Error
              ? analyticsQuery.error.message
              : 'Analytics could not be loaded. Try again shortly.'}
          </Alert>
        ) : null}

        {analytics && !mobile && (
          <GrowthOverview
            key={`${project.id}-${JSON.stringify(range)}`}
            projectId={project.id}
            analytics={analytics}
            showGoals={detailed}
          />
        )}

        {!detailed && ((summary?.events ?? 0) > 0 || mobile) && (
          <FindingsFeed projectId={project.id} />
        )}

        {detailed && <ExternalSignals projectId={project.id} range={range} />}

        {detailed && !mobile && (
          <SearchPerformance projectId={project.id} range={range} />
        )}

        {analyticsQuery.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-[430px] rounded-card" />
            <div className="grid gap-4 lg:grid-cols-2">
              <Skeleton className="h-80 rounded-card" />
              <Skeleton className="h-80 rounded-card" />
            </div>
          </div>
        ) : !mobile &&
          summary &&
          summary.events === 0 &&
          !summary.lastEventAt &&
          !realtimeQuery.data?.summary.lastEventAt &&
          range.key === '7d' ? (
          <ProjectSetup
            projectId={project.id}
            publicKey={activeKey?.publicKey}
          />
        ) : detailed && analytics && summary ? (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              {!mobile && (
                <BreakdownPanel
                  tabs={[
                    {
                      id: 'channel',
                      label: 'Channels',
                      itemLabel: 'Channel',
                      items: analytics.breakdowns.channel,
                      value: 'visitors',
                    },
                    {
                      id: 'source',
                      label: 'Sources',
                      itemLabel: 'Source',
                      items: analytics.breakdowns.source,
                      value: 'visitors',
                    },
                    {
                      id: 'campaign',
                      label: 'Campaigns',
                      itemLabel: 'Campaign',
                      items: analytics.breakdowns.campaign,
                      value: 'visitors',
                    },
                    {
                      id: 'referrer',
                      label: 'Referrers',
                      itemLabel: 'Referrer',
                      items: analytics.breakdowns.referrer,
                      value: 'visitors',
                    },
                  ]}
                />
              )}
              {!mobile && (
                <BreakdownPanel
                  tabs={[
                    {
                      id: 'page',
                      label: 'Pages',
                      itemLabel: 'Page',
                      items: analytics.breakdowns.page,
                      value: 'visitors',
                    },
                    {
                      id: 'entryPage',
                      label: 'Entry pages',
                      itemLabel: 'Entry page',
                      items: analytics.breakdowns.entryPage,
                      value: 'visitors',
                    },
                    {
                      id: 'exitPage',
                      label: 'Exit pages',
                      itemLabel: 'Exit page',
                      items: analytics.breakdowns.exitPage,
                      value: 'visitors',
                    },
                    {
                      id: 'outboundLink',
                      label: 'Outbound links',
                      itemLabel: 'Destination',
                      items: analytics.breakdowns.outboundLink,
                      value: 'visitors',
                    },
                  ]}
                />
              )}
              <BreakdownPanel
                tabs={[
                  {
                    id: 'event',
                    label: 'Product events',
                    itemLabel: 'Event',
                    items: analytics.breakdowns.event,
                    value: 'events',
                  },
                ]}
              />
              <BreakdownPanel
                tabs={[
                  {
                    id: 'country',
                    label: 'Countries',
                    itemLabel: 'Country',
                    items: analytics.breakdowns.country,
                    value: 'visitors',
                  },
                  {
                    id: 'region',
                    label: 'Regions',
                    itemLabel: 'Region',
                    items: analytics.breakdowns.region,
                    value: 'visitors',
                  },
                  {
                    id: 'city',
                    label: 'Cities',
                    itemLabel: 'City',
                    items: analytics.breakdowns.city,
                    value: 'visitors',
                  },
                ]}
              />
              <div className="lg:col-span-2">
                <BreakdownPanel
                  tabs={[
                    {
                      id: 'browser',
                      label: 'Browsers',
                      itemLabel: 'Browser',
                      items: analytics.breakdowns.browser,
                      value: 'visitors',
                    },
                    {
                      id: 'os',
                      label: 'Operating systems',
                      itemLabel: 'Operating system',
                      items: analytics.breakdowns.os,
                      value: 'visitors',
                    },
                    {
                      id: 'device',
                      label: 'Devices',
                      itemLabel: 'Device',
                      items: analytics.breakdowns.device,
                      value: 'visitors',
                    },
                  ]}
                />
              </div>
            </div>
          </>
        ) : null}
        {!detailed && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5 text-sm">
            <p className="text-ink-muted">
              {mobile
                ? 'Revenue first. App activity and other details are in Reports.'
                : 'Your growth at a glance. Explore the details when you need them.'}
            </p>
            <Button asChild variant="secondary">
              <Link to={`/projects/${project.id}/reports`}>
                Explore reports
              </Link>
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
